import { getFormattedName, concatAttendeeName } from '@cvent/event-dumping-ground';
import { OTHER_ATTENDEE_REGISTRATION_ID, REQUEST_DATE_STATUS, REQUEST_STATUS, ROOMMATE_MATCHING_TYPE } from './travelConstants';
import { createSelector, defaultMemoize } from 'reselect';
import { utcToDisplayDate } from '@cvent/event-core';
import { getRoommateRequestSettings, mapFromRoommateSetupSettings } from '../lib/HotelRequest/utils/HotelRoomUtil';
import { SURVEY_TYPE, QUESTION_DISPLAY_TYPE } from './questionConstants';
import { getIn } from 'icepick';
const MIN_SEARCH_LENGTH = 3;
export const RoommateIdType = {
    /**
     * contactId for No response invitee
     */
    CONTACT: 'CONTACT',
    /**
     * hotelReservationDetailId for Accepted invitee
     */
    HOTEL_RESERVATION_DETAIL: 'HOTEL_RESERVATION_DETAIL',
    /**
     * eventRegistrationId for primary invitee
     */
    EVENT_REGISTRATION: 'EVENT_REGISTRATION',
    /**
     * eventRegistrationId for guests
     */
    GUEST_EVENT_REGISTRATION: 'GUEST_EVENT_REGISTRATION'
};
/**
 * Roommate Matching Status for booked roommates
 */
export const RoommateStatus = {
    PENDING: 1,
    ACCEPTED: 2,
    PRE_SELECTED: 3,
    DENIED: 4,
    CANCELLED: 5
};
/*
 * Prints the room preferences for a particular booking preference
 */
export function printRoomPreferences(showPreferences, bookingPreferences, quantity, translate) {
    const isSingleRoomBooked = quantity === 1;
    if (!showPreferences) {
        return isSingleRoomBooked
            ? translate('EventWidgets_RegSummary_RoomRequested__resx')
            : translate('EventWidgets_RegSummary_RoomsRequested__resx', { number: quantity });
    }
    if (!bookingPreferences.isSmokingOpted && bookingPreferences.isAdaAccessibilityOpted) {
        return isSingleRoomBooked
            ? translate('EventWidgets_PasskeyHotelRequest_OneRoomWithNonSmokingAndADA__resx')
            : translate('EventWidgets_PasskeyHotelRequest_RoomsWithNonSmokingAndADA__resx', { number: quantity });
    }
    else if (bookingPreferences.isSmokingOpted && bookingPreferences.isAdaAccessibilityOpted) {
        return isSingleRoomBooked
            ? translate('EventWidgets_PasskeyHotelRequest_OneRoomWithSmokingAndADA__resx')
            : translate('EventWidgets_PasskeyHotelRequest_RoomsWithSmokingAndADA__resx', { number: quantity });
    }
    else if (!bookingPreferences.isSmokingOpted && !bookingPreferences.isAdaAccessibilityOpted) {
        return isSingleRoomBooked
            ? translate('EventWidgets_PasskeyHotelRequest_OneRoomWithNonSmoking__resx')
            : translate('EventWidgets_PasskeyHotelRequest_RoomsWithNonSmoking__resx', { number: quantity });
    }
    else if (bookingPreferences.isSmokingOpted && !bookingPreferences.isAdaAccessibilityOpted) {
        return isSingleRoomBooked
            ? translate('EventWidgets_PasskeyHotelRequest_RoomWithSmoking__resx')
            : translate('EventWidgets_PasskeyHotelRequest_RoomsWithSmoking__resx', { number: quantity });
    }
    return null;
}
export function makeAttendeeRegistrationIdForOther(primaryEventRegistrationId) {
    return `${OTHER_ATTENDEE_REGISTRATION_ID}~${primaryEventRegistrationId}`;
}
/**
 * Returns primary attendee display name
 * @returns {string}
 */
function getPrimaryAttendeeDisplayName({ firstName, middleName, lastName }, translate, nameFormat) {
    if (!firstName && !middleName && !lastName) {
        return translate('EventWidgets_AirRequest_PrimaryNoName__resx');
    }
    return getFormattedName({ name: { first: firstName, middle: middleName, last: lastName }, fields: nameFormat });
}
/**
 * make attendee options to display on ui
 * @param {Function} translate
 * @param {Object} registrations
 * @param {Object} registrations.primary primary event registration details
 * @param {Array} registrations.guests guests' registration details
 * @param {Object} settings
 * @param {boolean} settings.showGuests whether should show guest in the dropdown or not
 * @param {Object} settings.attendeeRequestCounts map of existing counts of air requests for the attendees
 * @param {number} settings.attendeeLimit per attendee air request limit
 * @param {boolean} settings.isForExisting whether this render is for an existing request or new request
 * @param {boolean} settings.isPlanner whether planner reg or not
 * @returns {Array}
 */
export function makeAttendeeOptions(translate, { primary = { attendee: { personalInformation: {} }, eventId: '' }, guests = [] } = {}, { showGuests = true, allowNonRegistrant = true, attendeeRequestCounts = {}, attendeeLimit, isForExisting, isPlanner, nameFormat }) {
    // return empty if don't need to render guests, because default selection value will be of primary
    if (!showGuests) {
        return [];
    }
    const ignoreLimit = isPlanner || isForExisting || !attendeeLimit;
    const attendeeOptionGroups = [];
    const primaryCanRequest = ignoreLimit || attendeeRequestCounts[primary.eventRegistrationId] < attendeeLimit;
    if (primaryCanRequest) {
        attendeeOptionGroups.push({
            category: translate('EventWidgets_AirRequest_Labels_Primary__resx'),
            items: [
                {
                    name: getPrimaryAttendeeDisplayName(primary.attendee.personalInformation, translate, nameFormat),
                    value: primary.eventRegistrationId
                }
            ]
        });
    }
    const guestAndOtherOptions = guests
        .filter(guest => ignoreLimit || attendeeRequestCounts[guest.eventRegistrationId] < attendeeLimit)
        .map(guest => ({
        name: getFormattedName({
            name: {
                first: guest.attendee.personalInformation.firstName,
                middle: guest.attendee.personalInformation.middleName,
                last: guest.attendee.personalInformation.lastName
            },
            fields: nameFormat
        }),
        value: guest.eventRegistrationId
    }));
    if (primaryCanRequest && allowNonRegistrant) {
        guestAndOtherOptions.push({
            name: translate('EventWidgets_AirRequest_Labels_Other__resx'),
            value: makeAttendeeRegistrationIdForOther(primary.eventRegistrationId)
        });
    }
    if (guestAndOtherOptions.length) {
        attendeeOptionGroups.push({
            category: translate('EventWidgets_AirRequest_Labels_Guests__resx'),
            items: guestAndOtherOptions
        });
    }
    return attendeeOptionGroups;
}
/**
 * Checks if search text provided matches min length criteria
 * @param {string} searchText
 * @returns {boolean}
 */
export function hasMinLengthForSearch(searchText) {
    return searchText && searchText.length >= MIN_SEARCH_LENGTH;
}
/**
 * Converts event startDate and endDate from UTC to display time
 * @param {Object} state
 * @returns {Object}
 */
export const transformEventDates = createSelector(state => state.event, state => state.timezones, (event, timezones) => {
    const timezone = timezones[event.timezone];
    return Object.assign(Object.assign({}, event), { startDate: utcToDisplayDate(event.startDate, timezone), endDate: utcToDisplayDate(event.endDate, timezone), timezone });
});
export const getRequestDateStatusOptions = (translate) => {
    return [
        {
            name: translate('EventWidgets_PlannerFields_ShoulderDates_Confirmed__resx'),
            value: REQUEST_DATE_STATUS.CONFIRMED
        },
        { name: translate('EventWidgets_PlannerFields_ShoulderDates_Pending__resx'), value: REQUEST_DATE_STATUS.PENDING },
        { name: translate('EventWidgets_PlannerFields_ShoulderDates_Denied__resx'), value: REQUEST_DATE_STATUS.DENIED }
    ];
};
/**
 * Returns translated request status values submitted by default.
 */
export const getRequestStatusKey = defaultMemoize(requestStatus => {
    switch (requestStatus) {
        case REQUEST_STATUS.SUBMITTED:
            return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusSubmitted__resx';
        case REQUEST_STATUS.PROCESSING:
            return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusProcessing__resx';
        case REQUEST_STATUS.APPROVED:
            return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusApproved__resx';
        case REQUEST_STATUS.RESERVED:
            return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusReserved__resx';
        case REQUEST_STATUS.DENIED:
            return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusDenied__resx';
        case REQUEST_STATUS.NOT_AVAILABLE:
            return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusNotAvailable__resx';
        case REQUEST_STATUS.CANCELLED:
            return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusCancelled__resx';
        case REQUEST_STATUS.PENDING:
            return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusPending__resx';
        case REQUEST_STATUS.TICKETED:
            return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusTicketed__resx';
        default:
            return 'EventWidgets_PlannerFields_PlannerFieldsReqStatusSubmitted__resx';
    }
});
/**
 * returns option object for request status listing
 */
const getRequestStatusOptionObject = defaultMemoize((translate, requestStatus) => {
    return { name: translate(getRequestStatusKey(requestStatus)), value: requestStatus };
});
/**
 * makes options list for request status dropdown control
 */
export const makeRequestStatusPlannerFieldOptions = defaultMemoize((translate, forAir) => {
    const options = [
        getRequestStatusOptionObject(translate, REQUEST_STATUS.SUBMITTED),
        getRequestStatusOptionObject(translate, REQUEST_STATUS.PROCESSING),
        getRequestStatusOptionObject(translate, REQUEST_STATUS.APPROVED),
        getRequestStatusOptionObject(translate, REQUEST_STATUS.RESERVED),
        getRequestStatusOptionObject(translate, REQUEST_STATUS.DENIED),
        getRequestStatusOptionObject(translate, REQUEST_STATUS.NOT_AVAILABLE),
        getRequestStatusOptionObject(translate, REQUEST_STATUS.CANCELLED),
        getRequestStatusOptionObject(translate, REQUEST_STATUS.PENDING)
    ];
    if (forAir) {
        options.push(getRequestStatusOptionObject(translate, REQUEST_STATUS.TICKETED));
    }
    return options;
});
/**
 * return merged roommate settings based on presence of roommateRequestSettings
 * @param configRoommateSettings
 * @param roommateRequestSettings
 * @returns {*}
 */
export const getMergedRoommateSettings = (configRoommateSettings, roommateRequestSettings) => {
    const defaultRoommateSettings = getRoommateRequestSettings();
    if (!roommateRequestSettings || !roommateRequestSettings.roommatePreferenceType) {
        return Object.assign(Object.assign({}, defaultRoommateSettings), mapFromRoommateSetupSettings(configRoommateSettings));
    }
    return null;
};
/**
 * Returns true if survery type belongs to travel questions
 * @param {string} surveyType
 * @returns {boolean}
 */
export function isTravelQuestion(surveyType) {
    return [
        SURVEY_TYPE.AIR_ACTUAL_QUESTIONS,
        SURVEY_TYPE.AIR_ACTUAL_ALTERNATE_QUESTIONS,
        SURVEY_TYPE.AIR_QUESTIONS,
        SURVEY_TYPE.AIR_ALTERNATE_QUESTIONS,
        SURVEY_TYPE.HOTEL_QUESTIONS,
        SURVEY_TYPE.HOTEL_ALTERNATE_QUESTIONS,
        SURVEY_TYPE.GROUP_FLIGHT_QUESTIONS,
        SURVEY_TYPE.GROUP_FLIGHT_ALTERNATE_QUESTIONS
    ].includes(surveyType);
}
/**
 * returns true if given survey type is hotel question.
 */
export const isHotelQuestion = (surveyType) => {
    return [SURVEY_TYPE.HOTEL_QUESTIONS, SURVEY_TYPE.HOTEL_ALTERNATE_QUESTIONS].includes(surveyType);
};
/**
 * Returns true if survery type belongs to travel alternate questions
 * @param {string} surveyType
 * @returns {boolean}
 */
export function isAlternateQuestion(surveyType) {
    return [
        SURVEY_TYPE.AIR_ACTUAL_ALTERNATE_QUESTIONS,
        SURVEY_TYPE.AIR_ALTERNATE_QUESTIONS,
        SURVEY_TYPE.HOTEL_ALTERNATE_QUESTIONS,
        SURVEY_TYPE.GROUP_FLIGHT_ALTERNATE_QUESTIONS
    ].includes(surveyType);
}
/**
 * Returns associations for travel questions
 * @param {string} surveyType
 * @returns {Array}
 */
export function getTravelQuestionAssociations(surveyType) {
    switch (surveyType) {
        case SURVEY_TYPE.AIR_ACTUAL_QUESTIONS:
        case SURVEY_TYPE.AIR_ACTUAL_ALTERNATE_QUESTIONS:
            return ['airActual'];
        case SURVEY_TYPE.AIR_QUESTIONS:
        case SURVEY_TYPE.AIR_ALTERNATE_QUESTIONS:
            return ['airRequest'];
        case SURVEY_TYPE.HOTEL_QUESTIONS:
        case SURVEY_TYPE.HOTEL_ALTERNATE_QUESTIONS:
            return ['hotelRequest'];
        case SURVEY_TYPE.GROUP_FLIGHT_QUESTIONS:
        case SURVEY_TYPE.GROUP_FLIGHT_ALTERNATE_QUESTIONS:
            return ['groupFlightRequest'];
        default:
            return [];
    }
}
/**
 * Air actual should be visible when:
 * 1. in site editor view
 * 2. for only planner if isPlannerDisplay option is set
 * and if display settings are present for air actual setup (use case when having different regPaths)
 * @type {boolean}
 */
export function shouldShowAirActualWidget(airActualSetup, isSiteEditorPreview, isPlannerRegistrationMode) {
    const isPlannerDisplayOnly = airActualSetup.isPlannerDisplayOnly;
    return airActualSetup.displaySettings && (isSiteEditorPreview || !isPlannerDisplayOnly || isPlannerRegistrationMode);
}
/**
 * Returns true if answers exist for provided metaData
 * @param {Object} questionsMetaData
 * @param {Array} travelAnswers
 * @returns {boolean}
 */
function hasTravelAnswers(questionsMetaData, travelAnswers) {
    const answersedQuestionIds = travelAnswers.map(a => a.questionId);
    return questionsMetaData.some(metaData => {
        return answersedQuestionIds.includes(metaData.question.id);
    });
}
/**
 * Returns whether to display travel questions on registration summary or not
 * @param {Object} questionsMetaData
 * @param {Array} travelAnswers
 * @param {Object} questionsConfig
 * @returns {boolean}
 */
export function displayTravelQuestions({ requestQuestionMetaData, alternateQuestionMetaData }, travelAnswers, questionsConfig) {
    return (hasTravelAnswers([...requestQuestionMetaData, ...alternateQuestionMetaData], travelAnswers) ||
        (questionsConfig.displayType === QUESTION_DISPLAY_TYPE.ALL && alternateQuestionMetaData.length > 0));
}
/**
 * Returns filtered question by survey type
 * @param {Array} questionsMetadata
 * @param {string} surveyType
 * @returns {Array}
 */
export function filterTravelQuestionsBySurveryType(questionsMetadata, surveyType) {
    return questionsMetadata.filter(q => q.question.isTravelQuestion && q.question.additionalInfo.surveyType === surveyType);
}
function getRoommateStatusFromRoommatePreferenceType(roommatePreferenceType, roommatePreferences, roomRequest, translate, nameFormat) {
    let roommateStatusText = '';
    switch (roommatePreferenceType) {
        case ROOMMATE_MATCHING_TYPE.NAME:
            if (roommatePreferences.roommateDescription) {
                roommateStatusText = roommatePreferences.roommateDescription;
                break;
            }
            else {
                return '';
            }
        case ROOMMATE_MATCHING_TYPE.ENLIST_SELF:
            roommateStatusText = `(${translate('EventWidgets_HotelRequest_AddedToTheList__resx')})`;
            break;
        case ROOMMATE_MATCHING_TYPE.SEARCH:
        case ROOMMATE_MATCHING_TYPE.ACCEPT:
            roommateStatusText = getRoommateStatusTextOnRoommateSelection(roomRequest, translate, nameFormat);
            break;
        default:
            if (roommatePreferences) {
                roommateStatusText = getRoommateStatusTextOnRoommateSelection(roomRequest, translate, nameFormat);
            }
            else {
                return '';
            }
    }
    return roommateStatusText;
}
export const getRoommateMatchingStatusText = defaultMemoize((roomRequest, translate, isSiteEditor, nameFormat) => {
    var _a;
    if (!roomRequest.isMultiOccupancyRoom || !roomRequest.preferences.isRoomSharingOpted) {
        return '';
    }
    else if (isSiteEditor) {
        return translate('EventWidgets_HotelRequest_LabelRoommate__resx');
    }
    const roommatePreferences = (_a = roomRequest.preferences) === null || _a === void 0 ? void 0 : _a.roommatePreferences;
    const roommatePreferenceType = roommatePreferences && roomRequest.preferences.roommatePreferences.type;
    const roommateStatusText = getRoommateStatusFromRoommatePreferenceType(roommatePreferenceType, roommatePreferences, roomRequest, translate, nameFormat);
    return roommateStatusText
        ? `${translate('EventWidgets_HotelRequest_LabelRoommate__resx')} ${roommateStatusText}`
        : '';
});
function checkIfRoommateNameBlank(roommateName) {
    let isRoommateNameBlank = false;
    roommateName.split(',').forEach(elem => {
        if (elem.trim().length > 0) {
            isRoommateNameBlank = true;
        }
    });
    return isRoommateNameBlank;
}
function getRoommateStatusTextOnRoommateSelection(roomRequest, translate, nameFormat) {
    var _a;
    let roommateStatusText = '';
    const roommatePreferences = roomRequest.preferences.roommatePreferences;
    let roommateName = getFormattedName({
        name: { first: roommatePreferences.roommateFirstName, last: roommatePreferences.roommateLastName },
        fields: nameFormat
    });
    // add additional attendee names
    if ((_a = roommatePreferences.additionalRoommates) === null || _a === void 0 ? void 0 : _a.length) {
        roommatePreferences.additionalRoommates.map(r => {
            roommateName = concatAttendeeName(roommateName, getFormattedName({
                name: { first: r.firstName, last: r.lastName },
                fields: nameFormat
            }));
        });
    }
    if (roomRequest.isRoommateMatchingFailed) {
        roommateStatusText = `(${translate('EventWidgets_HotelRequest_NoRoommateAssigned__resx')})`;
    }
    else if (roomRequest.preferences.roommatePreferences.roommateMatchingStatus === RoommateStatus.DENIED) {
        roommateStatusText = `(${translate('EventWidgets_HotelRequest_PreviousRoommateDenied__resx')})`;
    }
    else if (roomRequest.isRoommateMatched) {
        roommateStatusText = checkIfRoommateNameBlank(roommateName)
            ? `${roommateName} (${translate('EventWidgets_HotelRequest_ConfirmedRoommate__resx')})`
            : '';
    }
    else {
        roommateStatusText = checkIfRoommateNameBlank(roommateName)
            ? `${roommateName} (${translate('EventWidgets_HotelRequest_UnconfirmedRoommate__resx')})`
            : '';
    }
    return roommateStatusText;
}
/**
 * Returns Deem - travel integration enabled or not
 * @param {Object} state
 * @returns {boolean}
 */
export function isDeemEnabled(state) {
    return getIn(state, ['eventTravel', 'airData', 'isDeemEnabled']);
}
export const convertCapacitySummariesToCapacityMap = (capacitySummaries, regTypeId) => {
    const capacityMap = {};
    Object.values(capacitySummaries.hotelCapacities)
        .filter(hotel => hotel.roomCapacities)
        .map(hotel => Object.values(hotel.roomCapacities))
        .flat()
        .filter(room => room.dateCapacities)
        .map(room => Object.values(room.dateCapacities))
        .flat(2)
        .filter(capacity => capacity.contactTypeId === regTypeId && capacity.capacityId && capacity.totalReserveCapacityAvailable)
        .forEach(capacity => {
        capacityMap[capacity.capacityId] = {
            availableCapacity: capacity.totalReserveCapacityAvailable
        };
    });
    return capacityMap;
};
